import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./context/AuthContext";
import { PlanProvider } from "./context/PlanContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Pricing from "./pages/Pricing";
import Profile from "./pages/Profile";
import CheckoutReturn from "./pages/CheckoutReturn";
import Support from "./pages/Support";
import Admin from "./pages/Admin";
import Watchlists from "./pages/Watchlists";
import Alerts from "./pages/Alerts";
import Backtester from "./pages/Backtester";
import CommunityHome from "./pages/community/CommunityHome";
import PostDetail from "./pages/community/PostDetail";
import NewPost from "./pages/community/NewPost";
import FeatureRequests from "./pages/community/FeatureRequests";
import UserProfile from "./pages/community/UserProfile";
import Terms from "./pages/Terms";
import VerifyEmail from "./pages/VerifyEmail";
import LaunchControl from "./pages/LaunchControl";
import LaunchGate from "./components/LaunchGate";
import AdminPreviewBadge from "./components/AdminPreviewBadge";

function CommunityDetailWrapper() {
  const { slug } = useParams();
  return <CommunityHome params={{ slug }} />;
}
import Privacy from "./pages/Privacy";
import NotFound from "./pages/NotFound";
import FloatingHelp from "./components/FloatingHelp";
import ErrorBoundary from "./components/ErrorBoundary";
import TrialBanner from "./components/TrialBanner";
import useAnalytics from "./hooks/useAnalytics";

function Router() {
  const location = useLocation();
  useAnalytics();
  // synchronously detect Emergent Auth callback and handle before normal routes
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/support" element={<Support />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/admin/launch" element={<ProtectedRoute><LaunchControl /></ProtectedRoute>} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/checkout-return" element={<ProtectedRoute><CheckoutReturn /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/watchlists" element={<ProtectedRoute><Watchlists /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/backtester" element={<ProtectedRoute><Backtester /></ProtectedRoute>} />
      <Route path="/community" element={<CommunityHome />} />
      <Route path="/community/new" element={<ProtectedRoute><NewPost /></ProtectedRoute>} />
      <Route path="/community/feature-requests" element={<FeatureRequests />} />
      <Route path="/community/post/:post_id" element={<PostDetail />} />
      <Route path="/community/u/:username" element={<UserProfile />} />
      <Route path="/community/:slug" element={<CommunityDetailWrapper />} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:px-3 focus:py-2 focus:bg-white focus:text-black focus:rounded-md focus:text-sm focus:font-semibold focus:outline focus:outline-2 focus:outline-[var(--bull)]"
      >
        Skip to main content
      </a>
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <PlanProvider>
              <TrialBanner />
              <LaunchGate>
                <Router />
                <AdminPreviewBadge />
              </LaunchGate>
              <FloatingHelp />
              <Toaster
                position="top-right"
                theme="dark"
                toastOptions={{
                  style: {
                    background: "rgba(20,20,26,0.85)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
                    fontSize: "13px",
                    backdropFilter: "blur(20px)",
                  },
                }}
              />
            </PlanProvider>
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
